/**
 * ExamCard Component
 * Displays a single exam card in the Question World exam list
 */

import React, { useState } from 'react';
import { Card, Typography, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { BookOutlined, ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { formatDuration } from '../helpers/utils';
import StartExamModal from './ExamComponent/StartExamModal';
import { useDispatch } from 'react-redux';
import { resetSegmentedExamSubject } from '../../../../../stateManager/reducers/examSlice';
import { setStatesToInitial } from '../../../../../stateManager/reducers/studentAuthSlice';
import '../styles/ExamCard.css';

const { Title, Text } = Typography;

/**
 * ExamCard - Displays exam information in a card format
 * @param {Object} props
 * @param {Object} props.exam - Exam data object
 * @param {string} props.exam.examId._id - Exam ID
 * @param {string} props.exam.examId.title - Exam title
 * @param {number} props.exam.duration - Duration in minutes
 * @param {Array} props.exam.examId.questions - Questions array
 * @param {string} props.groupId - Group ID
 * @param {string} props.groupName - Group/Batch name
 */
const ExamCard = ({ exam, groupId, groupName, allSubjects, ...props }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isInstructionModalVisible, setInstructionModalVisible] = useState(false);
  
  var isInstantPracticeExam = false;
  if (props.pageType === "question-world") {
    isInstantPracticeExam = true;
  }

  // Handle start practice click
  const handleStartPractice = () => {
    history.push(`/question-world/exam/${exam?.examId?._id}/${groupId}`);
  };

  // Handle start exam click - clear localStorage and open modal
  const handleStartExam = async () => {
    // Clear previous exam data from localStorage
    localStorage.removeItem('exam_candidateCategory');
    localStorage.removeItem('exam_faculty');
    localStorage.removeItem('exam_subject');
    localStorage.removeItem('subjectOfSelectedExam');
    localStorage.removeItem('allSubjects');
    localStorage.removeItem('selectedExamdetails');
    
    // Clear all previous exam state for fresh start
    localStorage.removeItem('localSavedAnswers');
    localStorage.removeItem('isLive');
    localStorage.removeItem('examMetaData');
    localStorage.removeItem('selectedSegmentedExamSubjects');
    
    // Reset Redux states for fresh start
    await dispatch(resetSegmentedExamSubject());
    dispatch(setStatesToInitial('noOfAnsweredQuestions'));
    dispatch(setStatesToInitial('answerQuestions'));
    
    // Open instruction modal
    setInstructionModalVisible(true);
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setInstructionModalVisible(false);
  };

  // Handle modal ok (after exam starts)
  const handleModalOk = () => {
    setInstructionModalVisible(false);
  };

  // Get question count
  const questionCount = exam?.examId?.isSegmentedExam 
    ? exam?.examId?.segmentedExamDetails?.totalQuestions 
    : exam?.examId?.questions?.length || 0;

  return (
    <>
      <StartExamModal
        visible={isInstructionModalVisible}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        exam={exam}
        allSubjects={allSubjects || []}
        groupId={groupId}
      />
      <Card className="qw-exam-card" hoverable>
        <div className='qw-exam-card-content'>
        {/* Exam Title */}
        <Title level={5} className="qw-exam-card-title" ellipsis={{ rows: 2 }}>
          {exam?.examId?.title || 'Untitled Exam'}
        </Title>

        {/* Meta Information */}
        <div className="qw-exam-card-meta">
          {(groupName && !isInstantPracticeExam) && (
            <div className="qw-exam-card-meta-item">
              <BookOutlined style={{ color: '#1890ff' }} />
              <Text>{groupName}</Text>
            </div>
          )}

          <div className="qw-exam-card-meta-item">
            <QuestionCircleOutlined style={{ color: '#faad14' }} />
            <Text>{questionCount} Questions</Text>
          </div>

          { !isInstantPracticeExam && (
            <div className="qw-exam-card-meta-item">
              <ClockCircleOutlined style={{ color: '#52c41a' }} />
              <Text>{formatDuration(exam?.duration)}</Text>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="qw-exam-card-buttons">
        <Button
          className="qw-exam-card-btn qw-btn-practice"
          onClick={handleStartPractice}
        >
          Start Practice
        </Button>
        <Button
          className="qw-exam-card-btn qw-btn-exam"
          onClick={handleStartExam}
        >
          Start Exam
        </Button>
      </div>
    </Card>
    </>
  );
};

export default ExamCard;

